import { FC, memo, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { useAppModalControls } from '@/common/hooks';
import { messageOnError } from '@/common/utils';

import {
    ExternalRedirectsQueryKeys,
    useBulkDeleteExternalRedirectsMutation,
    useGetExternalRedirectsConflictsQuery,
} from '@/modules/ExternalRedirects';

import { ExternalRedirectsConflictsView } from './ExternalRedirectsConflictsView';

interface Props {
  siteId: string;
}

export const ExternalRedirectsConflictsContainer: FC<Props> = memo(({ siteId }) => {
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [controls] = useAppModalControls();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data: conflicts } = useGetExternalRedirectsConflictsQuery(siteId)();

  const redundant = conflicts?.redundant ?? [];
  const cyclic = conflicts?.cyclic ?? [];
  const totalCount = redundant.length + cyclic.length;

  const { mutate: bulkDelete, isLoading: isDeleting } = useBulkDeleteExternalRedirectsMutation(siteId)({
    onSuccess: async () => {
      await Promise.all([
        queryClient.refetchQueries([ExternalRedirectsQueryKeys.Conflicts]),
        queryClient.refetchQueries([ExternalRedirectsQueryKeys.GetAll]),
      ]);
      appMessageApi.success('Проблемні зовнішні редіректи видалено');
      setSelectedIds([]);
    },
    onError: (err) => messageOnError(err, appMessageApi),
  });

  const handleToggleId = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (!selectedIds.length) return;
    bulkDelete({ ids: selectedIds });
  }, [selectedIds, bulkDelete]);

  const redundantIds = useMemo(() => redundant.map(({ redirect }) => redirect.id), [redundant]);

  const handleDeleteAllRedundant = useCallback(() => {
    if (!redundantIds.length) return;
    bulkDelete({ ids: redundantIds });
  }, [redundantIds, bulkDelete]);

  return (
    <ExternalRedirectsConflictsView
      controls={ controls }
      conflicts={{ redundant, cyclic }}
      totalCount={ totalCount }
      selectedIds={ selectedIds }
      isDeleting={ isDeleting }
      onToggleId={ handleToggleId }
      onDeleteSelected={ handleDeleteSelected }
      onDeleteAllRedundant={ handleDeleteAllRedundant }
    />
  );
});
